import { useState, useEffect } from 'react';
import { Calendar, Clock, AlertTriangle, Users, Info, ExternalLink, Shield, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ProgressRing } from '@/components/ui/progress-ring';
import { api } from '@/lib/api/config';
import { isValidDomain } from '@/lib/utils/validation';
import { searchDomainBreaches, type BreachEntry } from '@/lib/api/breach';

interface Breach {
  Name: string;
  Title: string;
  Domain: string;
  BreachDate: string;
  AddedDate: string;
  ModifiedDate: string;
  PwnCount: number;
  Description: string;
  DataClasses: string[];
  IsVerified: boolean;
  IsFabricated: boolean;
  IsSensitive: boolean;
  IsRetired: boolean;
  IsSpamList: boolean;
  IsMalware: boolean;
}

const StatusBadge = ({ label, status, invert = false }: { label: string; status: boolean; invert?: boolean }) => {
  const isPositive = invert ? !status : status;
  
  return (
    <div className={`flex items-center p-2 rounded ${
      isPositive ? 'bg-green-50 dark:bg-green-950' : 'bg-muted'
    }`}>
      <div className={`w-3 h-3 rounded-full mr-2 ${
        isPositive ? 'bg-green-500' : 'bg-muted-foreground'
      }`}></div>
      <span className={`text-sm ${
        isPositive ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'
      }`}>
        {label}
      </span>
    </div>
  );
};

export function BreachMonitoring() {
  const [breaches, setBreaches] = useState<Breach[]>([]);
  const [filteredBreaches, setFilteredBreaches] = useState<Breach[]>([]);
  const [selectedBreach, setSelectedBreach] = useState<Breach | null>(null);
  const [searchResults, setSearchResults] = useState<BreachEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [searchProgress, setSearchProgress] = useState(0);
  const [progressInterval, setProgressInterval] = useState<number | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);

  useEffect(() => {
    const fetchBreaches = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/breach/latest');
        const breachData = response.data;
        setBreaches(breachData);
        setFilteredBreaches(breachData);
      } catch (err) {
        console.error('Error fetching breach data:', err);
        setError('Failed to fetch breach data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBreaches();
  }, []);

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setSearchError(null);
    setSearchResults([]);
    setHasSearched(false);
    
    const formData = new FormData(e.target as HTMLFormElement);
    const query = formData.get('domain') as string;

    if (!query.trim()) {
      setSearchError('Please enter a domain to search');
      return;
    }

    const { isValid, cleanDomain } = isValidDomain(query);
    if (!isValid) {
      setSearchError('Please enter a valid domain name');
      return;
    }

    try {
      // Reset and start progress animation
      setSearching(true);
      setSearchProgress(0);
      const interval = window.setInterval(() => {
        setSearchProgress(prev => {
          if (prev >= 90) return prev; // Cap at 90% until complete
          return prev + Math.random() * 15;
        });
      }, 500);
      setProgressInterval(interval);

      const response = await searchDomainBreaches(cleanDomain);
      setSearchResults(response.entries || []);
      setSearchProgress(100);
      setHasSearched(true);
    } catch (err) {
      console.error('Error searching breaches:', err);
      setSearchError('Failed to search breaches. Please try again.');
      setSearchResults([]);
      setHasSearched(true);
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
        setProgressInterval(null);
      }
      setSearching(false);
    }
  };

  const handleBreachSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value.toLowerCase();
    const filtered = breaches.filter(breach => 
      breach.Title.toLowerCase().includes(searchTerm) ||
      breach.Name.toLowerCase().includes(searchTerm)
    );
    setFilteredBreaches(filtered);
  };

  const togglePassword = (id: string) => {
    setShowPassword(prev => {
      // If password is already revealed, keep it revealed
      if (prev[id]) return prev;
      // Otherwise reveal it
      return {
        ...prev,
        [id]: true
      };
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-ibm-condensed">Leaked Data Search</h1>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            name="domain"
            placeholder="Enter your organization's domain (e.g. acme.com)"
            className="pl-9"
          />
        </div>
        <Button type="submit" disabled={searching}>
          {searching ? (
            <>
              <ProgressRing className="mr-2 text-primary-foreground" />
              Searching...
            </>
          ) : (
            'Search'
          )}
        </Button> 
      </form>

      {searching && (
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">
            Searching through breach databases...
          </div>
          <Progress value={searchProgress} className="h-1" />
        </div>
      )}

      {searchError && (
        <div className="text-sm text-destructive">
          {searchError}
        </div>
      )}

      {hasSearched && searchResults && searchResults.length === 0 && !searching && !searchError && (
        <Card>
          <CardContent className="p-6 text-center">
            <div className="flex flex-col items-center justify-center py-8">
              <div className="rounded-full bg-muted p-6 mb-4">
                <Shield className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-medium mb-2">No leaked data found</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                We couldn't find any leaked data for this domain in our database. This is a good sign, but remember that our database only includes known breaches.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {searchResults && searchResults.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {searchResults
            .sort((a, b) => {
              // First sort by password presence
              if (a.password && !b.password) return -1;
              if (!a.password && b.password) return 1;
              
              // Then sort by hashed password presence
              if (a.hashed_password && !b.hashed_password) return -1;
              if (!a.hashed_password && b.hashed_password) return 1;
              
              // Keep original order for remaining items
              return 0;
            })
            .map((entry) => (
            <Card key={entry.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-left">{entry.name || entry.email}</CardTitle>
                  <span className="text-sm text-muted-foreground">{entry.database_name}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {entry.email && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Email:</span>
                    <span>{entry.email}</span>
                  </div>
                )}
                {entry.username && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Username:</span>
                    <span>{entry.username}</span>
                  </div>
                )}
                {entry.password && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Password:</span>
                    <span 
                      className={`relative cursor-pointer group text-left ${!showPassword[entry.id] ? 'blur-sm select-none' : 'select-text'}`}
                      onClick={() => togglePassword(entry.id)}
                    >
                      {entry.password}
                      {!showPassword[entry.id] && (
                        <span className="absolute left-full ml-2 px-2 py-1 bg-popover rounded text-xs opacity-0 group-hover:opacity-100 whitespace-nowrap">
                          Press to Reveal
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {entry.hashed_password && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Hashed Password:</span>
                    <span 
                      className={`relative cursor-pointer group text-left ${!showPassword[entry.id] ? 'blur-sm select-none' : 'select-text'}`}
                      onClick={() => togglePassword(entry.id)}
                    >
                      {entry.hashed_password}
                      {!showPassword[entry.id] && (
                        <span className="absolute left-full ml-2 px-2 py-1 bg-popover rounded text-xs opacity-0 group-hover:opacity-100 whitespace-nowrap">
                          Press to Reveal
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {entry.name && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Name:</span>
                    <span>{entry.name}</span>
                  </div>
                )}
                {entry.phone && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Phone:</span>
                    <span>{entry.phone}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {loading ? (
        <Card>
          <CardContent className="h-[100%] flex items-center justify-center">
            <div className="text-muted-foreground">Loading breach data...</div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center text-destructive mb-4">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <h2 className="text-xl font-semibold">Error</h2>
            </div>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <div className={`grid ${selectedBreach ? 'grid-cols-1 md:grid-cols-2 gap-6' : 'grid-cols-1'}`}>
          <div>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-4">
                  <CardTitle className="text-2xl font-ibm-condensed font-normal">
                    Data Breaches ({filteredBreaches.length})
                  </CardTitle>
                  <div className="relative w-[300px]">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                      placeholder="Search breaches"
                      className="pl-9"
                      onChange={handleBreachSearch}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2 h-[calc(68vh)] overflow-y-auto pr-1">
                  {filteredBreaches.map(breach => (
                    <div 
                      key={breach.Name}
                      onClick={() => setSelectedBreach(breach)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedBreach?.Name === breach.Name 
                          ? 'bg-accent' 
                          : 'hover:bg-accent'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <h3 className="font-medium">{breach.Title}</h3>
                        <span className="text-xs px-2 py-1 rounded-full bg-destructive/10 text-destructive">
                          {formatNumber(breach.PwnCount)} affected
                        </span>
                      </div>
                      
                      <div className="mt-2 text-sm text-muted-foreground flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>Breach: {formatDate(breach.BreachDate)}</span>
                      </div>
                      
                      <div className="mt-1 text-sm text-muted-foreground flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>Added: {formatDate(breach.AddedDate)}</span>
                      </div>
                      
                      <div className="mt-2 flex flex-wrap gap-1">
                        {breach.DataClasses.slice(0, 3).map(dataClass => (
                          <span key={dataClass} className="px-2 py-0.5 text-xs rounded-full bg-muted">
                            {dataClass}
                          </span>
                        ))}
                        {breach.DataClasses.length > 3 && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-muted">
                            +{breach.DataClasses.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {selectedBreach && (
            <div>
              <Card>
                <CardContent className="p-6 h-[calc(100vh-15.5rem)] overflow-y-auto">
                  <div className="flex justify-between items-start mb-6">
                    <h2 className="text-2xl font-ibm-condensed">{selectedBreach.Title}</h2>
                    {selectedBreach.Domain && (
                      <a 
                        href={`https://${selectedBreach.Domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-sm text-primary hover:text-primary/80"
                      >
                        <span>{selectedBreach.Domain}</span>
                        <ExternalLink className="h-4 w-4 ml-1" />
                      </a>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm font-medium text-muted-foreground mb-1">Breach Date</div>
                        <div className="flex items-center text-lg">
                          <Calendar className="h-5 w-5 mr-2 text-orange-500" />
                          {formatDate(selectedBreach.BreachDate)}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm font-medium text-muted-foreground mb-1">Added to Database</div>
                        <div className="flex items-center text-lg">
                          <Clock className="h-5 w-5 mr-2 text-green-500" />
                          {formatDate(selectedBreach.AddedDate)}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm font-medium text-muted-foreground mb-1">Last Modified</div>
                        <div className="flex items-center text-lg">
                          <Info className="h-5 w-5 mr-2 text-primary" />
                          {formatDate(selectedBreach.ModifiedDate)}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm font-medium text-muted-foreground mb-1">Affected Users</div>
                        <div className="flex items-center text-lg">
                          <Users className="h-5 w-5 mr-2 text-destructive" />
                          {formatNumber(selectedBreach.PwnCount)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">Description</h3>
                    <p 
                      className="text-muted-foreground [&_a]:underline [&_a]:text-primary hover:[&_a]:text-primary/80"
                      dangerouslySetInnerHTML={{ __html: selectedBreach.Description }}
                    />
                  </div>
                  
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">Compromised Data</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedBreach.DataClasses.map(dataClass => (
                        <span key={dataClass} className="px-3 py-1 rounded-full bg-destructive/10 text-destructive text-sm">
                          {dataClass}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="border-t border-border pt-4">
                    <h3 className="text-lg font-semibold mb-2">Breach Status</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <StatusBadge 
                        label="Verified" 
                        status={selectedBreach.IsVerified} 
                      />
                      <StatusBadge 
                        label="Fabricated" 
                        status={selectedBreach.IsFabricated} 
                        invert={true}
                      />
                      <StatusBadge 
                        label="Retired" 
                        status={selectedBreach.IsRetired} 
                        invert={true}
                      />
                      <StatusBadge 
                        label="Spam List" 
                        status={selectedBreach.IsSpamList} 
                        invert={true}
                      />
                      <StatusBadge 
                        label="Malware" 
                        status={selectedBreach.IsMalware} 
                        invert={true}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}